import { compareValuesNullsLast } from './utils'
import { formatScientific, coerceNumberOrTrimmedString } from '@/utils/formatters'
import {
  getPossibleConversionsForUnit,
  convertValueToUnit,
} from '@/components/combined-predict/components/CombinedPredictTable/utils/unitConversions'
import { shouldHideField } from './utils'
import { isEmpty, get } from 'lodash-es'

const isPredictedField = (field) => !!field && typeof field === 'string' && field?.includes('predicted')
const isExperimentalField = (field) => !!field && typeof field === 'string' && field?.includes('experimental')

const getModelPredictionRoot = (predictionsByModelId, modelId) =>
  modelId ? get(predictionsByModelId, modelId, null) : null

const getChemicalFromExtras = (extrasByModelId, modelId) => {
  const restForModel = getModelPredictionRoot(extrasByModelId, modelId)
  return {
    standardizedChemical: restForModel?.standardizedChemical ?? null,
    sourceChemical: restForModel?.sourceChemical ?? null,
  }
}

const hasErrorForModel = (errorsByModelId, modelId) => !isEmpty(get(errorsByModelId, modelId))

const ERROR_DISPLAY_VALUE = '-'
const tooltipErrorDisplayValue = 'Model returned an error'

const returnEmptySignIfError = (errorsByModelId, modelId) =>
  hasErrorForModel(errorsByModelId, modelId) ? ERROR_DISPLAY_VALUE : null

const getFormattedValue = ({ params, column, formatFunction }) =>
  returnEmptySignIfError(params?.data?.errorsByModelId, column?.modelId) ?? formatFunction()

const EMPTY_DISPLAY_VALUE = ''

const formatValueWithUnit = (value, unit) => formatScientific(value, { blank: EMPTY_DISPLAY_VALUE, unit })

const formatValueWithConversion = ({ value, fromUnit, toUnit, mass }) => {
  const valueToFormat = coerceNumberOrTrimmedString(value)
  if (valueToFormat === null) return EMPTY_DISPLAY_VALUE
  if (typeof valueToFormat === 'string') return valueToFormat

  return !!fromUnit && !!toUnit && fromUnit !== toUnit
    ? formatValueWithUnit(convertValueToUnit({ value: valueToFormat, fromUnit, toUnit, mass }), toUnit)
    : formatValueWithUnit(valueToFormat, toUnit)
}

const buildHeaderTooltip = (column) => {
  if (column?.children || (!column?.description && !column?.name)) return undefined
  return `${column?.description ?? column?.name}${column?.unit ? `, ${column.unit}` : ''}`
}

const getDefaultCellClass = ({ isLinkCell, isExperimental, customClasses, params, column }) => [
  'ag-right-aligned-cell',
  ...(isExperimental ? ['bg-success-light'] : []),
  ...(!!isLinkCell && !hasErrorForModel(params?.data?.errorsByModelId, column?.modelId)
    ? ['text-primary link-cell']
    : []),
  ...(customClasses ? customClasses : []),
]

const buildDefaultColumnDef = ({ column, minWidth = 70, autoHeaderHeight = true, ...columnDefOverrides }) => {
  return {
    headerName: column?.name,
    field: column?.id,
    autoHeaderHeight,
    minWidth,
    ...columnDefOverrides,
  }
}

const buildGroupColumnDef = ({ column, ...columnDefOverrides }) => {
  const possibleConversions = getPossibleConversionsForUnit(column?.unit)

  return buildDefaultColumnDef({
    column,

    headerGroupComponentParams: () => ({
      field: column?.id,
      unit: column.groupId ? undefined : column?.unit,
      sourceName: column?.sourceName,
      description: column?.description,
      parentDescription: column?.parentDescription,
      ...(possibleConversions.length ? { possibleConversions } : {}),
    }),

    ...columnDefOverrides,
    marryChildren: true,
  })
}

const buildColumnDef = ({
  column,
  visibleColumnIds,
  tableSettings,
  type = ['numericColumn'],
  hide = shouldHideField({ fieldKey: column?.id, tableSettings, visibleColumnIds }),
  customCellClasses = [],
  ...columnDefOverrides
}) => {
  const headerTooltip = buildHeaderTooltip(column)

  const isExperimental = isExperimentalField(column?.id)
  const isPredicted = isPredictedField(column?.id)

  return buildDefaultColumnDef({
    column,
    type,

    hide: hide,

    tooltipValueGetter: (params) =>
      params?.valueFormatted === ERROR_DISPLAY_VALUE ? tooltipErrorDisplayValue : params?.valueFormatted,

    cellClass: (params) =>
      getDefaultCellClass({
        isLinkCell: params?.value?.link && !!isPredicted,
        isExperimental,
        customClasses: customCellClasses,
        params,
        column,
      }),

    comparator: (aValue, bValue, _nodeA, _nodeB, isDescending) => compareValuesNullsLast(aValue, bValue, isDescending),

    ...(headerTooltip ? { headerTooltip } : {}),

    ...columnDefOverrides,
  })
}

export {
  isPredictedField,
  isExperimentalField,
  getDefaultCellClass,
  formatValueWithConversion,
  buildColumnDef,
  buildGroupColumnDef,
  getModelPredictionRoot,
  getFormattedValue,
  ERROR_DISPLAY_VALUE,
  tooltipErrorDisplayValue,
  getChemicalFromExtras,
}
